import { FC, memo } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import Box from '@amzn/awsui-components-core/box';
import Checkbox from '@amzn/awsui-components-core/checkbox';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import FormField from '@amzn/awsui-components-core/form-field';
import Button from '@amzn/awsui-components-core/button';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';

// ============================================================================
// BankCardGroup — reusable sub-component rendered once per card type
// ============================================================================

interface BankCardGroupProps {
    /** Heading label for this card type (e.g. "Credit Cards") */
    label: string;
    /** Card code → display name pairs from config */
    cardOptions: [string, string][];
    /** Currently selected card codes */
    selectedCards: string[];
    /** Callback to update selected card codes */
    setSelectedCards: React.Dispatch<React.SetStateAction<string[]>>;
    /** Tenure dropdown options */
    tenureOptions: SelectProps.Option[];
    /** Currently selected tenure value */
    tenureValue: string;
    /** Callback to update tenure value */
    setTenureValue: React.Dispatch<React.SetStateAction<string>>;
    /** Label for the tenure dropdown */
    tenureLabel: string;
    /** Placeholder for the tenure dropdown */
    tenurePlaceholder: string;
    /** Whether to show the "Check all" button (credit/debit only) */
    showCheckAll?: boolean;
    /** Label for the "Check all" button */
    checkAllLabel?: string;
    /** data-testid prefix for this card group (e.g. "credit-card") */
    testIdPrefix: string;
    /** Whether all interactive elements in this group are disabled */
    disabled?: boolean;
}

const BankCardGroup: FC<BankCardGroupProps> = ({
    label,
    cardOptions,
    selectedCards,
    setSelectedCards,
    tenureOptions,
    tenureValue,
    setTenureValue,
    tenureLabel,
    tenurePlaceholder,
    showCheckAll = false,
    checkAllLabel,
    testIdPrefix,
    disabled = false,
}) => {
    // In view mode, if there are no card options configured, show a dash instead of empty dropdowns
    if (disabled && cardOptions.length === 0) {
        return (
            <SpaceBetween size="s">
                <Box variant="h3">{label}</Box>
                <Box color="text-status-inactive">—</Box>
            </SpaceBetween>
        );
    }

    return (
    <SpaceBetween size="s">
        <Box variant="h3">{label}</Box>
        {showCheckAll && !disabled && (
            <Button
                onClick={() => {
                    const allCodes = cardOptions.map(([k]) => k);
                    setSelectedCards((prev) => {
                        // Upstream normalization (useNcemiFormState) guarantees selectedCards
                        // always contains code keys, so matching by code alone is safe.
                        const allSelected = allCodes.length > 0 && allCodes.every(
                            (code) => prev.includes(code),
                        );
                        return allSelected ? [] : allCodes;
                    });
                }}
                data-testid={`check-all-${testIdPrefix}s`}
            >
                {checkAllLabel}
            </Button>
        )}
        {cardOptions.map(([code, name]) => {
            // Upstream normalization (useNcemiFormState) converts API display names to
            // config code keys at the boundary, so selectedCards always contains codes.
            const isChecked = selectedCards.includes(code);
            return (
                <Checkbox
                    key={code}
                    checked={isChecked}
                    onChange={({ detail }) => {
                        setSelectedCards((prev) =>
                            detail.checked
                                ? [...prev, code]
                                : prev.filter((c) => c !== code),
                        );
                    }}
                    disabled={disabled}
                    data-testid={`${testIdPrefix}-${code}`}
                >
                    {name}
                </Checkbox>
            );
        })}
        <FormField label={tenureLabel}>
            <Select
                selectedOption={
                    tenureValue
                        ? tenureOptions.find((o) => o.value === tenureValue) ?? null
                        : null
                }
                onChange={({ detail }) =>
                    setTenureValue(detail.selectedOption.value ?? '')
                }
                options={tenureOptions}
                placeholder={tenurePlaceholder}
                ariaRequired={true}
                disabled={disabled}
                data-testid={`${testIdPrefix}-tenure`}
            />
        </FormField>
    </SpaceBetween>
    );
};

// ============================================================================
// NcemiBankCardSection — main exported component
// ============================================================================

export interface NcemiBankCardSectionProps extends SectionBaseProps {
    /** Credit card code → name pairs */
    creditCardOptions: [string, string][];
    /** CBCC card code → name pairs */
    cbccCardOptions: [string, string][];
    /** BFL card code → name pairs */
    bflCardOptions: [string, string][];
    /** Currently selected credit card codes */
    selectedCreditCards: string[];
    /** Callback to update selected credit card codes */
    setSelectedCreditCards: React.Dispatch<React.SetStateAction<string[]>>;
    /** Currently selected CBCC card codes */
    selectedCbccCards: string[];
    /** Callback to update selected CBCC card codes */
    setSelectedCbccCards: React.Dispatch<React.SetStateAction<string[]>>;
    /** Currently selected BFL card codes */
    selectedBflCards: string[];
    /** Callback to update selected BFL card codes */
    setSelectedBflCards: React.Dispatch<React.SetStateAction<string[]>>;
    /** Selected credit card tenure value */
    creditCardTenure: string;
    /** Callback to update credit card tenure */
    setCreditCardTenure: React.Dispatch<React.SetStateAction<string>>;
    /** Selected CBCC card tenure value */
    cbccCardTenure: string;
    /** Callback to update CBCC card tenure */
    setCbccCardTenure: React.Dispatch<React.SetStateAction<string>>;
    /** Selected BFL card tenure value */
    bflCardTenure: string;
    /** Callback to update BFL card tenure */
    setBflCardTenure: React.Dispatch<React.SetStateAction<string>>;
    /** Credit card tenure dropdown options */
    creditTenureOptions: SelectProps.Option[];
    /** CBCC card tenure dropdown options */
    cbccTenureOptions: SelectProps.Option[];
    /** BFL card tenure dropdown options */
    bflTenureOptions: SelectProps.Option[];
}

/**
 * NCEMI bank card guardrails section — renders 3 card type groups
 * Each group has checkboxes for card codes, a tenure dropdown, and optional "Check all".
 * Only shown for NO_COST_EMI campaign type (parent controls visibility).
 */
const NcemiBankCardSection: FC<NcemiBankCardSectionProps> = ({
    creditCardOptions,
    cbccCardOptions,
    bflCardOptions,
    selectedCreditCards,
    setSelectedCreditCards,
    selectedCbccCards,
    setSelectedCbccCards,
    selectedBflCards,
    setSelectedBflCards,
    creditCardTenure,
    setCreditCardTenure,
    cbccCardTenure,
    setCbccCardTenure,
    bflCardTenure,
    setBflCardTenure,
    creditTenureOptions,
    cbccTenureOptions,
    bflTenureOptions,
    mode,
    disabled,
}) => {
    const { translate } = useTranslate('organisms.ncemiBankCardSection');
    const isDisabled = disabled ?? mode === 'view';

    return (
        <Container header={<Header variant="h2">{translate('section_bank_card_guardrails')}</Header>}>
            <SpaceBetween size={mode === 'view' ? 's' : 'l'}>
                {/* Row 1: Credit Cards (left) + Debit Cards (right) */}
                <ColumnLayout columns={2}>
                    <BankCardGroup
                        label={translate('credit_cards_label')}
                        cardOptions={creditCardOptions}
                        selectedCards={selectedCreditCards}
                        setSelectedCards={setSelectedCreditCards}
                        tenureOptions={creditTenureOptions}
                        tenureValue={creditCardTenure}
                        setTenureValue={setCreditCardTenure}
                        tenureLabel={translate('credit_card_tenure_label')}
                        tenurePlaceholder={translate('tenure_placeholder')}
                        showCheckAll={true}
                        checkAllLabel={translate('check_all')}
                        testIdPrefix="credit-card"
                        disabled={isDisabled}
                    />

                    {/* Debit card group disabled — see INP-2742 / CR-41902404 */}

                    {/* Col 2: CBCC (Up) + BFL (Down) */}
                    <ColumnLayout borders="horizontal" >
                        <BankCardGroup
                            label={translate('cbcc_cards_label')}
                            cardOptions={cbccCardOptions}
                            selectedCards={selectedCbccCards}
                            setSelectedCards={setSelectedCbccCards}
                            tenureOptions={cbccTenureOptions}
                            tenureValue={cbccCardTenure}
                            setTenureValue={setCbccCardTenure}
                            tenureLabel={translate('cbcc_card_tenure_label')}
                            tenurePlaceholder={translate('tenure_placeholder')}
                            testIdPrefix="cbcc-card"
                            disabled={isDisabled}
                        />
                        <BankCardGroup
                            label={translate('bfl_cards_label')}
                            cardOptions={bflCardOptions}
                            selectedCards={selectedBflCards}
                            setSelectedCards={setSelectedBflCards}
                            tenureOptions={bflTenureOptions}
                            tenureValue={bflCardTenure}
                            setTenureValue={setBflCardTenure}
                            tenureLabel={translate('bfl_card_tenure_label')}
                            tenurePlaceholder={translate('tenure_placeholder')}
                            testIdPrefix="bfl-card"
                            disabled={isDisabled}
                        />
                    </ColumnLayout>
                </ColumnLayout>
            </SpaceBetween>
        </Container>
    );
};

export default memo(NcemiBankCardSection);
